import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation, NavLink } from 'react-router-dom';
import { ASSET_DISPOSAL_API } from '@app/utils/apiUrl';
import { URI_INTERNAL } from '../../utils/pathLocations';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '@app/utils/httpClient';
import { Post, Put } from '@app/utils/httpProvider';
import Pagination from 'react-js-pagination';
import moment from 'moment';
import { ConfirmModal } from '../../modules/modal/ConfirmModal';

const AssetDisposalList = () => {
    const history = useHistory();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    
    const [data, setData] = useState({ data: [], totalRecords: 0 });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(Number(query.get('page') || 1));
    const [pageSize, setPageSize] = useState(Number(query.get('size') || 10));
    const [search, setSearch] = useState(query.get('search') || '');
    const [valueSearch, setValueSearch] = useState(query.get('search') || '');
    
    // Hủy phiếu
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isExportingId, setIsExportingId] = useState(null);

    const handleExportExcel = async (id, code) => {
        setIsExportingId(id);
        try {
            const response = await fetchWithAuth(ASSET_DISPOSAL_API.EXPORT_EXCEL(id), {
                method: 'GET'
            });

            if (response.ok) {
                const blob = await response.blob();
                const fileName = `PhieuDaoThai_${code || id}.xlsx`;
                
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success('Tải phiếu đào thải thành công!');
            } else {
                toast.error('Có lỗi xảy ra khi tải file.');
            }
        } catch (error) {
            console.error('Lỗi export excel:', error);
            toast.error('Lỗi kết nối máy chủ.');
        } finally {
            setIsExportingId(null);
        }
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const payload = {
                pageIndex: page,
                pageSize: pageSize,
                keyword: valueSearch || ''
            };
            const response = await Post(ASSET_DISPOSAL_API.GET_PAGING, null, payload);
            if (response && response.ok) {
                if (response.status === 204) {
                    setData({ data: [], totalRecords: 0 });
                } else {
                    const result = await response.json();
                    if (result) {
                        setData(result);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching disposal list:', error);
            toast.error('Lỗi khi tải danh sách phiếu đào thải!');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, valueSearch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const changePage = (number) => {
        setPage(number);
    };

    const handleCancel = async () => {
        if (!selectedId) return;
        setOpenConfirmModal(false);
        try {
            const res = await Put(`${ASSET_DISPOSAL_API.CANCEL}/${selectedId}`);
            if (res && res.ok) {
                toast.success('Hủy phiếu đào thải thành công!');
                fetchData();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi hủy phiếu');
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header bg-white pt-3 pb-0 border-bottom-0">
                            <ul className="nav nav-tabs" style={{ marginBottom: '-1px' }}>
                                <li className="nav-item">
                                    <span 
                                        className="nav-link text-muted" 
                                        style={{ cursor: 'pointer' }} 
                                        onClick={() => history.push(URI_INTERNAL.INVENTORY_SESSION_PATH)}
                                    >
                                        <i className="fas fa-clipboard-list mr-2" />
                                        Danh sách Phiếu chốt kiểm kê
                                    </span>
                                </li>
                                <li className="nav-item">
                                    <span className="nav-link active font-weight-bold text-danger" style={{ cursor: 'pointer', borderTop: '3px solid #dc3545' }}>
                                        <i className="fas fa-trash-alt mr-2" />
                                        Danh sách Phiếu đào thải
                                    </span>
                                </li>
                            </ul>
                        </div>
                        <div className="card-body">
                            <div className="row mb-3 align-items-center">
                                <div className="col-12 col-md-auto order-1 order-md-2 ml-md-auto mb-3 mb-md-0">
                                    <div className="d-flex justify-content-center justify-content-md-end">
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => history.push(URI_INTERNAL.ASSET_DISPOSAL_CREATE_PATH)}
                                        >
                                            <i className="fas fa-plus mr-2"></i> Lập phiếu mới
                                        </button>
                                    </div>
                                </div>
                                <div className="col-12 col-md order-2 order-md-1">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Tìm kiếm theo mã phiếu, tiêu đề..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    setPage(1);
                                                    setValueSearch(search);
                                                }
                                            }}
                                        />
                                        <div className="input-group-append">
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => {
                                                    setPage(1);
                                                    setValueSearch(search);
                                                }}
                                            >
                                                <i className="fas fa-search"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center p-4">
                                    <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
                                    <p className="mt-2 text-muted">Đang tải dữ liệu...</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-striped table-bordered table-hover mb-0 mobile-card-view">
                                        <thead className="thead-light">
                                            <tr>
                                                <th className="text-center" style={{ width: '50px' }}>STT</th>
                                                <th>Mã phiếu</th>
                                                <th>Tiêu đề</th>
                                                <th className="text-center">Ngày tạo</th>
                                                <th>Người tạo</th>
                                                <th className="text-center">Số lượng tài sản</th>
                                                <th className="text-center">Trạng thái</th>
                                                <th className="text-center">Hành động</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.data?.length > 0 ? (
                                                data.data.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td className="text-center align-middle" data-label="STT">
                                                            {(page - 1) * pageSize + index + 1}
                                                        </td>
                                                        <td className="align-middle font-weight-bold text-dark" data-label="Mã phiếu">{item.disposalCode}</td>
                                                        <td className="align-middle" data-label="Tiêu đề">{item.title}</td>
                                                        <td className="text-center align-middle" data-label="Ngày tạo">{moment(item.disposalDate).format('DD/MM/YYYY HH:mm')}</td>
                                                        <td className="align-middle" data-label="Người tạo">{item.createdBy}</td>
                                                        <td className="text-center align-middle text-danger font-weight-bold" data-label="Số lượng tài sản">
                                                            {item.totalAssets}
                                                        </td>
                                                        <td className="text-center align-middle" data-label="Trạng thái">
                                                            {item.status === 1 ? (
                                                                <span className="badge badge-success px-2 py-1" style={{ borderRadius: '4px' }}>Đã chốt</span>
                                                            ) : item.status === 2 ? (
                                                                <span className="badge badge-danger px-2 py-1" style={{ borderRadius: '4px' }}>Đã hủy</span>
                                                            ) : (
                                                                <span className="badge badge-secondary px-2 py-1" style={{ borderRadius: '4px' }}>Nháp</span>
                                                            )}
                                                        </td>
                                                        <td className="text-center align-middle text-nowrap" data-label="Hành động">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-success btn-sm"
                                                                title="Xuất Excel"
                                                                style={{ marginRight: '6px' }}
                                                                onClick={() => handleExportExcel(item.id, item.disposalCode)}
                                                                disabled={isExportingId === item.id}
                                                            >
                                                                <i className={isExportingId === item.id ? "fas fa-spinner fa-spin" : "fas fa-file-excel"} />
                                                            </button>
                                                            <NavLink
                                                                to={`${URI_INTERNAL.ASSET_DISPOSAL_PATH}/chi-tiet/${item.id}`}
                                                                className="btn btn-outline-info btn-sm"
                                                                title="Xem chi tiết"
                                                                style={{ marginRight: item.status === 1 ? '6px' : '0' }}
                                                            >
                                                                <i className="fas fa-eye" />
                                                            </NavLink>
                                                            {item.status === 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    title="Hủy phiếu"
                                                                    onClick={() => {
                                                                        setSelectedId(item.id);
                                                                        setOpenConfirmModal(true);
                                                                    }}
                                                                >
                                                                    <i className="fas fa-ban" />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-4">
                                                        <div className="text-muted">
                                                            <i className="fas fa-box-open fa-2x mb-2 d-block"></i>
                                                            Không có dữ liệu phiếu đào thải
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {data?.totalRecords > 0 && (
                                <div className="row mt-3">
                                    <div className="col-sm-12 d-flex justify-content-center">
                                        <Pagination
                                            itemClass="page-item"
                                            linkClass="page-link"
                                            innerClass="pagination pagination-sm mb-0"
                                            activePage={page}
                                            itemsCountPerPage={Number(pageSize)}
                                            totalItemsCount={data?.totalRecords || 0}
                                            pageRangeDisplayed={5}
                                            onChange={changePage}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={openConfirmModal}
                confirmType="save"
                variant="danger"
                acceptText="Xác nhận Hủy"
                handleAccept={handleCancel}
                handleCancel={() => setOpenConfirmModal(false)}
                title="Xác nhận hủy phiếu"
                content="Bạn có chắc chắn muốn hủy phiếu đào thải này? Các tài sản sẽ được phục hồi lại trạng thái cũ."
            />
        </div>
    );
};

export default AssetDisposalList;
